import React from "react";
import { Alert, Button, Card, Col, Modal, Row, Space, Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  EditOutlined,
  SafetyCertificateOutlined,
  FileExcelOutlined,
  DollarCircleOutlined,
  ColumnWidthOutlined,
  WarningOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { RiEarthLine } from "react-icons/ri";
import { Globe2Icon } from "lucide-react";
import { PRIMARY } from "../../CustomUtils.js/utils";
import Flex from "components/shared-components/Flex";

const { Text, Paragraph } = Typography;

const STEPS = [
  {
    icon: <EditOutlined style={{ fontSize: 22, color: "#389e0d" }} />,
    title: "Include country code",
    description: (
      <>
        Enter every mobile number with country code (digits only, no{" "}
        <code style={{ color: "#cf1322", background: "#fff1f0", padding: "0 4px", borderRadius: 3 }}>+</code>{" "}
        sign required).
      </>
    ),
  },
  {
    icon: <SafetyCertificateOutlined style={{ fontSize: 22, color: "#389e0d" }} />,
    title: "No automatic prefix",
    description: (
      <>
        The system will not automatically add{" "}
        <Text strong style={{ color: "#cf1322" }}>91</Text>{" "}
        or any other country prefix in this mode.
      </>
    ),
  },
  {
    icon: <ColumnWidthOutlined style={{ fontSize: 22, color: "#389e0d" }} />,
    title: "Number length",
    description: (
      <>
        Each number should be{" "}
        <Text strong style={{ color: "#389e0d" }}>8–15 digits</Text>{" "}
        including the country code.
      </>
    ),
  },
  {
    icon: <FileExcelOutlined style={{ fontSize: 22, color: "#389e0d" }} />,
    title: "Excel upload",
    description: (
      <>
        First column must contain full international number with country code.
      </>
    ),
  },
  {
    icon: <DollarCircleOutlined style={{ fontSize: 22, color: "#389e0d" }} />,
    title: "International pricing",
    description: (
      <>
        Charges vary by destination country based on your active gateway.
      </>
    ),
  },
];

const EXAMPLES = [
  { flag: "🇺🇸", country: "United States", number: "14155552671" },
  { flag: "🇮🇳", country: "India", number: "919876543210" },
];


const InternationalModeInfoModal = ({ open, onClose }) => {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      width={1200}
      styles={{ body: { padding: "0 24px 16px" } }}
      title={
        <div className="d-flex align-items-center" style={{ gap: 14, padding: "4px 0" }}>
          <GlobalOutlined style={{ fontSize: 22, color: PRIMARY }} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#141414", lineHeight: 1.3 }}>
              International Messaging Enabled
            </div>
            <div style={{ fontSize: 13, color: "#8c8c8c", fontWeight: 400 }}>
              Send messages worldwide using full international numbers.
            </div>
          </div>
        </div>
      }
      footer={
        <Flex justifyContent="end" style={{ gap: 10 }}>
          <Button onClick={onClose} size="medium">
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            icon={<CheckCircleOutlined />}
            onClick={onClose}
          >
            Got it
          </Button>
        </Flex>
      }
    >
      {/* Step-by-step flow */}
      <div className="d-flex align-items-start" style={{ gap: 4, marginBottom: 24, marginTop: 8 }}>
        {STEPS.map((step, idx) => (
          <React.Fragment key={idx}>
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                minWidth: 0,
              }}
            >
              {/* Icon bubble */}
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: "#f6ffed",
                  border: "2px solid #b7eb8f",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 8,
                  flexShrink: 0,
                }}
              >
                {step.icon}
              </div>
              <Text strong style={{ color: "#389e0d", fontSize: 12, marginBottom: 4, display: "block" }}>
                {step.title}
              </Text>
              <Text style={{ fontSize: 11.5, color: "#595959", lineHeight: 1.5 }}>
                {step.description}
              </Text>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                style={{
                  width: 28,
                  borderTop: "2px dashed #b7eb8f",
                  marginTop: 24,
                  flexShrink: 0,
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Bottom two panels */}
      <Row gutter={[16, 16]}>
        {/* Examples panel */}
        <Col xs={24} md={8}>
          <Card
            bordered
          >
            <div className="d-flex align-items-center mb-2" style={{ gap: 6 }}>
              <ThunderboltOutlined style={{ color: "#52c41a", fontSize: 14 }} />
              <Text strong style={{ color: "#389e0d", fontSize: 13 }}>
                Examples
              </Text>
            </div>
            <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 8 }}>
              {EXAMPLES.map((ex) => (
                <div key={ex.country} className="d-flex align-items-center" style={{ gap: 5 }}>
                  <span style={{ fontSize: 16, lineHeight: 1 }}>{ex.flag}</span>
                  <Text strong style={{ fontSize: 11.5, color: "#2b2b2b", marginRight: 2 }}>
                    {ex.country === "United States" ? "US" : ex.country}
                  </Text>
                  <Tag>
                    {ex.number}
                  </Tag>
                </div>
              ))}
            </div>
          </Card>
        </Col>

        {/* Warnings panel */}
        <Col xs={24} md={16}>
          <Alert
            message={
              <Text strong style={{ color: "#d46b08", fontSize: 13 }}>
                Important
              </Text>
            }
            description={
              <div style={{ fontSize: 12.5 }}>
                <Paragraph style={{ marginBottom: 6, color: "#614700" }}>
                  Only numbers for countries with an <strong>Active Countries</strong> will be allowed.
                </Paragraph>
                <Paragraph style={{ marginBottom: 6, color: "#614700" }}>
                  Numbers must be entered exactly as provided by the recipient.
                </Paragraph>
                <Paragraph style={{ fontWeight: 'bold', marginBottom: 0, color: "#614700" }}>
                  Do not include spaces, brackets, or &quot;+&quot; signs.
                </Paragraph>
              </div>
            }
            type="warning"
            showIcon
            icon={<WarningOutlined style={{ color: "#fa8c16", fontSize: 16 }} />}
            style={{
              background: "#fffbe6",
              border: "1px solid #ffe58f",
              borderRadius: 10,
              padding: "12px 14px",
              height: "100%",
            }}
          />
        </Col>
      </Row>
    </Modal>
  );
};

export default InternationalModeInfoModal;
